<template>
  <div>
    <search
      class="search-form -m-l-m"
      response-key="coaches"
      placeholder="Search coaches"
      :mutate-history="false"
      :allow-add="false"
      :small-input="true"
      :display-all-on-empty="true"
      readable-model-name="coach"
      title-classes="grid grid-cols-2 p-l-m m-t-xxs p-l-sm-xs-only p-r-n-xs-only align-items-center"
      results-container-classes="m-t-md grid sm-grid-cols-1 md-grid-cols-1 lg-grid-cols-2 grid-cols-3 gap-m"
      result-count-classes="m-b-sm p-l-m p-l-sm-xs-only p-r-n-xs-only"
      base-url="/api/v1/coaches"
    >
      <template v-slot:list="slotProps">
        <div class="pl-coach-card">
          <div class="flex align-items-center gap-sm">
            <img
              v-if="slotProps.result.avatar"
              :src="slotProps.result.avatar"
              class="pl-coach-avatar"
              alt="Coach avatar"
            />
            <div
              v-else
              class="pl-coach-avatar bg-gray-200 flex align-items-center justify-center flex-shrink-0"
            >
              <span class="text-gray-500 font-semi text-16">{{
                initials(slotProps.result)
              }}</span>
            </div>
            <div class="flex-1" style="min-width: 0">
              <div class="flex flex-col align-items-start gap-y-xxs">
                <h2 class="font-semi m-y-none text-16">
                  {{ slotProps.result.full_name }}
                </h2>
                <span
                  class="pl-badge"
                  :class="setupStatus(slotProps.result).cssClass"
                  :title="setupStatus(slotProps.result).title"
                >
                  {{ setupStatus(slotProps.result).label }}
                </span>
              </div>
              <div class="pl-setup-progress m-t-xs">
                <div
                  class="pl-setup-progress-fill"
                  :class="setupStatus(slotProps.result).cssClass"
                  :style="{ width: `${progressPercent(slotProps.result)}%` }"
                ></div>
              </div>
            </div>
          </div>

          <ul class="pl-coach-checklist m-t-sm m-b-none">
            <li :class="{ 'is-done': hasPhoto(slotProps.result) }">
              <i
                :class="
                  hasPhoto(slotProps.result)
                    ? 'fa-solid fa-circle-check text-green-500'
                    : 'fa-regular fa-circle text-gray-400'
                "
              ></i>
              {{ hasPhoto(slotProps.result) ? "Photo added" : "No photo yet" }}
            </li>
            <li :class="{ 'is-done': hasBio(slotProps.result) }">
              <i
                :class="
                  hasBio(slotProps.result)
                    ? 'fa-solid fa-circle-check text-green-500'
                    : 'fa-regular fa-circle text-gray-400'
                "
              ></i>
              {{ hasBio(slotProps.result) ? "Bio set" : "No bio set" }}
            </li>
            <li :class="{ 'is-done': hasPricing(slotProps.result) }">
              <i
                :class="
                  hasPricing(slotProps.result)
                    ? 'fa-solid fa-circle-check text-green-500'
                    : 'fa-regular fa-circle text-gray-400'
                "
              ></i>
              <span class="pl-checklist-text">{{
                pricingText(slotProps.result)
              }}</span>
            </li>
            <li
              :class="{ 'is-done': slotProps.result.accepting_private_lessons }"
            >
              <i
                :class="
                  slotProps.result.accepting_private_lessons
                    ? 'fa-solid fa-circle-check text-green-500'
                    : 'fa-regular fa-circle text-gray-400'
                "
              ></i>
              {{
                slotProps.result.accepting_private_lessons
                  ? "Accepting private lessons"
                  : "Not accepting private lessons"
              }}
            </li>
          </ul>

          <!-- Public sign-up URL for NEW families (existing members book via
               the app's private lessons tab), surfaced so admins can grab any
               coach's link without impersonating them. Hidden until the coach
               is bookable — same rule as coach home — since nobody can
               complete a booking on the link before then. -->
          <div v-if="isBookable(slotProps.result) && slotProps.result.lessons_url" class="m-t-sm" title="Share with new families who aren't on WrestlingIQ yet — current members book from the private lessons tab in the app.">
            <p class="text-12 font-semi text-gray-600 m-b-none">New-client link</p>
            <div class="flex align-items-center gap-x-xs">
              <span class="text-12 text-gray-600 pl-truncate" style="min-width: 0;">{{ slotProps.result.lessons_url }}</span>
              <copy-icon :text="slotProps.result.lessons_url" class="flex-shrink-0"></copy-icon>
            </div>
          </div>

          <!-- Emphasis follows the phase: pre-bookable, setup is the only job,
               so Continue setup leads (phrased as navigation, not "Finish",
               which read as a done button). Once bookable the ongoing
               destination is the dashboard — Manage demotes to a quiet
               secondary. -->
          <div class="flex gap-x-xs m-t-m">
            <button
              type="button"
              class="btn btn-compact"
              :class="isBookable(slotProps.result) ? 'btn-default' : 'btn-primary flex-1'"
              @click="openDrawer(slotProps.result)"
            >
              {{ isBookable(slotProps.result) ? "Manage" : "Continue setup" }}
              <i v-if="!isBookable(slotProps.result)" class="fa-solid fa-chevron-right m-l-xxs"></i>
            </button>
            <a
              :href="`/private_lessons/coaches/${slotProps.result.id}/dashboard`"
              class="btn btn-compact"
              :class="isBookable(slotProps.result) ? 'btn-primary flex-1' : 'btn-default'"
              :title="`${slotProps.result.first_name}'s private lesson dashboard`"
            >
              <i class="fa-solid fa-calendar-days m-r-xxs"></i> Dashboard
            </a>
          </div>

          <!-- Handoff note once nothing here needs the admin: the coach owns
               the day-to-day from their own device -->
          <p v-if="isFullySetUp(slotProps.result)" class="text-12 text-gray-600 m-t-xs m-b-none" data-cy="coach-card-handoff">
            {{ slotProps.result.first_name }} can run everything else — availability and bookings — from the WrestlingIQ app.
          </p>
        </div>
      </template>
    </search>

    <private-lesson-coach-drawer
      v-if="drawerCoach"
      :key="drawerCoach.id"
      :coach="drawerCoach"
      :currency="currency"
      :can-edit-pricing="canEditPricing"
      :endpoint="endpoint"
      :access-key="accessKey"
      :cloudfront-base-url="cloudfrontBaseUrl"
      :existing-attachments="drawerCoach.profile_photos || []"
      @close="closeDrawer"
      @saved="onDrawerSaved"
    ></private-lesson-coach-drawer>
  </div>
</template>

<script>
import Search from '../../shared/search.vue';
import CopyIcon from './copy_icon.vue';
import PrivateLessonCoachDrawer from './private_lesson_coach_drawer.vue';
import { coachSetupStatusMix } from './mixins/coach_setup_status_mix';

export default {
  name: 'private-lesson-coach-setup',
  components: {
    Search,
    CopyIcon,
    PrivateLessonCoachDrawer,
  },
  mixins: [coachSetupStatusMix],
  props: {
    currency: {
      type: String,
      default: 'usd',
    },
    endpoint: {
      type: String,
      required: true,
    },
    accessKey: {
      type: String,
      required: true,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: true,
    },
    canEditPricing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      drawerCoach: null,
    };
  },
  methods: {
    openDrawer(coach) {
      this.drawerCoach = coach;
    },
    closeDrawer() {
      this.drawerCoach = null;
    },
    onDrawerSaved() {
      const coach = this.drawerCoach;
      axios
        .get(this.$apiService.coachUrl(coach.id))
        .then((response) => {
          // drawerCoach is the same object instance the search results render,
          // so assigning refreshed data updates the card in place.
          Object.assign(coach, response.data);
          this.closeDrawer();
        })
        .catch(() => {
          // Save succeeded but the refresh failed — fall back to a reload so
          // the card doesn't show stale data.
          window.location.reload();
        });
    },
  },
};
</script>
